// @ts-check

/** @file Pure product-state projection for the sync status indicator and detail sheet. */

/** @import {SyncCoordinatorStatus} from "velocious/build/src/sync/sync-coordinator-types.js" */

/** @typedef {{enabled: boolean}} SyncQueueProviderState Offline mutation-queue provider state used by the queued-changes badge guard. */
/** @typedef {{labelKey: string, testID: string, textKey: string, textParams?: Record<string, string>}} SyncStatusDetailRow One detail-sheet row (translation keys; the renderer interpolates). */
/** @typedef {{details: SyncStatusDetailRow[], labelKey: string, pendingCount: number, queueBadgeKey: string | null, reasonKey: string | null, retryable: boolean, tone: "danger" | "info" | "warning"}} SyncStatusSurface Banner + detail-sheet projection. */

/** Stable transport/application failure codes mapped to human-readable reason keys. */
const SYNC_REASON_LABELS = Object.freeze({
  "authentication-required": "Sync sign-in required",
  "database-pool-timeout": "Database connection timeout",
  "network-unavailable": "No network connection",
  "project-routing-metadata-missing": "Project sync settings are incomplete",
  "sync-failed": "Sync failed",
  "sync-request-rejected": "Sync request rejected"
})

/**
 * Maps a safe coordinator failure code to a human-readable translation key.
 * @param {string} code Stable failure code from the coordinator.
 * @returns {string} Translation key for the reason.
 */
export function syncFailureReasonLabel(code) {
  return SYNC_REASON_LABELS[code] || "Sync failed"
}

/**
 * Projects reusable coordinator state onto AwesomeTasks labels and tones.
 * @param {SyncCoordinatorStatus} status Coordinator snapshot.
 * @returns {{label: string, retryable: boolean, tone: "danger" | "info" | "warning"} | null} Product presentation.
 */
export function projectSyncStatusPresentation(status) {
  switch (status.state) {
    case "offline": return {label: "Offline", retryable: true, tone: "warning"}
    case "pending": return {label: "Pending", retryable: false, tone: "warning"}
    case "syncing": return {label: "Syncing", retryable: false, tone: "info"}
    case "backoff": return {label: "Failed", retryable: true, tone: "danger"}
    case "failed": return {label: "Failed", retryable: status.failure?.retryable === true, tone: "danger"}
    case "conflicted": return {label: "Conflicted", retryable: false, tone: "danger"}
    default: return null
  }
}

/**
 * Projects coordinator state plus the offline mutation-queue provider onto the
 * full user-visible surface: banner label, human-readable reason, queued
 * changes badge, detail-sheet rows, and the retry decision. Pure and privacy-
 * safe: only stable codes, counts, and timestamps are projected, never
 * payloads or raw error text.
 * @param {SyncCoordinatorStatus} status Coordinator snapshot.
 * @param {SyncQueueProviderState} providerState Offline queue provider state; the badge is hidden when the provider is disabled.
 * @returns {SyncStatusSurface | null} Surface projection, or null when the banner must hide (idle/stopped).
 */
export function projectSyncStatusSurface(status, providerState) {
  const presentation = projectSyncStatusPresentation(status)
  if (!presentation) return null

  const failure = status.failure
  const reasonKey = failure
    ? syncFailureReasonLabel(failure.code)
    : status.state === "offline" ? "No network connection" : null
  const queueBadgeKey = providerState.enabled && status.pendingCount > 0 ? "%{count} changes queued" : null
  /** @type {SyncStatusDetailRow[]} */
  const details = []

  if (reasonKey) details.push({labelKey: "Reason", testID: "reason", textKey: reasonKey})
  if (status.pendingCount > 0) details.push({labelKey: "Queued changes", testID: "pendingCount", textKey: "%{count} changes queued", textParams: {count: String(status.pendingCount)}})
  if (status.rejectedCount > 0) details.push({labelKey: "Rejected changes", testID: "rejectedCount", textKey: "%{count} changes rejected", textParams: {count: String(status.rejectedCount)}})
  if (status.conflicts.length > 0) details.push({labelKey: "Conflicts", testID: "conflicts", textKey: "%{count} conflicts", textParams: {count: String(status.conflicts.length)}})
  if (status.lastSuccessAt) details.push({labelKey: "Last synced", testID: "lastSuccessAt", textKey: "%{date} last synced", textParams: {date: status.lastSuccessAt}})
  if (status.nextRetryAt) details.push({labelKey: "Next retry", testID: "nextRetryAt", textKey: "Next retry %{date}", textParams: {date: status.nextRetryAt}})

  return {
    details,
    labelKey: presentation.label,
    pendingCount: status.pendingCount,
    queueBadgeKey,
    reasonKey,
    retryable: presentation.retryable,
    tone: presentation.tone
  }
}
